// frontend/src/pages/Pricing.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Sidebar from "../components/Sidebar";
import {
  Check,
  Zap,
  Building2,
  Crown,
  FileText,
  Lock,
  Clock,
  ArrowRight,
  ShieldCheck,
  Scale,
  Sparkles,
  RefreshCw,
} from "lucide-react";
import { supabase } from "../lib/supabaseClient";
import useEntitlement from "../hooks/useEntitlement";

// Pricing is sourced from Supabase public.plans.
// Stripe remains authoritative for billing via stripe_price_id,
// and Supabase plans.price_usd_cents is authoritative for display.
// © 2026 Advanced Advisory Services, LLC. All Rights Reserved.

function badgeClasses(kind) {
  const base =
    "inline-flex items-center rounded-full px-3 py-1 text-[11px] font-black uppercase tracking-widest border";
  if (kind === "popular") return `${base} bg-slate-900 text-white border-slate-900`;
  if (kind === "consultant") return `${base} bg-amber-500 text-white border-amber-500`;
  if (kind === "current") return `${base} bg-emerald-600 text-white border-emerald-600`;
  return `${base} bg-white text-slate-700 border-slate-200`;
}

function cardBorder(flag, isCurrent) {
  if (isCurrent) return "border-emerald-500 shadow-lg";
  if (flag === "Most Popular") return "border-slate-900 shadow-lg";
  if (flag === "Best for Consultants") return "border-amber-400 shadow-xl shadow-amber-100";
  return "border-slate-200 hover:border-slate-300 hover:shadow-md";
}

function moneyFromCents(cents) {
  const x = Number(cents);
  if (!Number.isFinite(x)) return "—";
  return (x / 100).toLocaleString("en-US", { maximumFractionDigits: 0 });
}

function parseNextFromLocation(location) {
  try {
    const qs = new URLSearchParams(location.search || "");
    const next = qs.get("next");
    return next ? String(next) : "";
  } catch {
    return "";
  }
}

// Map plan_key -> UI presentation (icons, badges, notes)
// IMPORTANT: plan_key values must match public.plans exactly.
function uiForPlanKey(planKey) {
  const k = String(planKey || "").toLowerCase();

  if (k === "precision_pass") {
    return {
      icon: Zap,
      flag: null,
      note: "24-hour access window",
      intent: "paid",
      descriptionFallback: "Single audit for 1 business entity",
    };
  }

  if (k === "pro") {
    return {
      icon: FileText,
      flag: "Most Popular",
      note: null,
      intent: "paid",
      descriptionFallback: "Recurring audits with exports",
    };
  }

  if (k === "enterprise") {
    return {
      icon: Building2,
      flag: null,
      note: null,
      intent: "paid",
      descriptionFallback: "Operational remediation + retest",
    };
  }

  if (k === "global_pro") {
    return {
      icon: Crown,
      flag: "Best for Consultants",
      note: null,
      intent: "paid",
      descriptionFallback: "Multi-entity + consultant workflow",
    };
  }

  // Fallback for any new plans you add later
  return {
    icon: Sparkles,
    flag: null,
    note: null,
    intent: "paid",
    descriptionFallback: "Plan details",
  };
}

function labelForBillingType(billingType, period) {
  const bt = String(billingType || "").toLowerCase();
  if (bt === "one_time") return "One-Time";
  const p = String(period || "").toLowerCase();
  if (p === "year" || p === "annual" || p === "yearly") return "/year";
  return "/month";
}

function safeBool(v) {
  return v === true || v === "true" || v === 1;
}

function buildFeaturesFromPlanRow(plan) {
  const allowReports = safeBool(plan?.allow_reports);
  const allowRemediation = safeBool(plan?.allow_remediation);
  const consultant = safeBool(plan?.allow_consultant_workflow);
  const maxBusinesses = plan?.max_businesses == null ? null : Number(plan.max_businesses);
  const auditsPerPeriod = plan?.audits_per_period == null ? null : Number(plan.audits_per_period);

  const unlimitedBusinesses = maxBusinesses == null;
  const singleBusiness = maxBusinesses === 1;
  const unlimitedAudits = auditsPerPeriod == null;

  return [
    { text: unlimitedAudits ? "Unlimited audits" : "Multiple audits/month", included: true },
    { text: "Unlimited access", included: true },
    {
      text: unlimitedBusinesses
        ? "Unlimited business entities"
        : singleBusiness
        ? "1 business entity"
        : `${maxBusinesses} business entities`,
      included: true,
    },
    { text: "Multi-jurisdiction mapping", included: true },
    { text: "PDF report exports", included: allowReports },
    { text: "Remediation tracking", included: allowRemediation },
    { text: "Legal trace & citations", included: allowReports },
    { text: "Consultant dashboard", included: consultant },
    { text: "Client entity management", included: consultant },
  ].filter(Boolean);
}

export default function Pricing() {
  const navigate = useNavigate();
  const location = useLocation();

  const nextParam = useMemo(() => parseNextFromLocation(location), [location]);

  const [authed, setAuthed] = useState(false);

  const [plansState, setPlansState] = useState({
    status: "loading", // loading | ready | error
    rows: [],
    error: null,
  });

  // Entitlement (source of truth for "current plan")
  const {
    loading: entLoading,
    isPaid,
    rawPlanKey,
    paidStatus,
    refresh: refreshEntitlement,
  } = useEntitlement();

  const currentPlanKey = useMemo(() => {
    if (!isPaid) return "";
    return String(rawPlanKey || "");
  }, [isPaid, rawPlanKey]);

  useEffect(() => {
    let mounted = true;

    (async () => {
      try {
        const { data } = await supabase.auth.getSession();
        const ok = Boolean(data?.session?.access_token);
        if (mounted) setAuthed(ok);
      } catch {
        if (mounted) setAuthed(false);
      }
    })();

    return () => {
      mounted = false;
    };
  }, []);

  // Load plans (public)
  useEffect(() => {
    let mounted = true;

    (async () => {
      try {
        setPlansState({ status: "loading", rows: [], error: null });

        const { data, error } = await supabase
          .from("plans")
          .select(
            "plan_key, display_name, billing_type, price_usd_cents, stripe_price_id, stripe_product_id, is_active, allow_reports, allow_remediation, max_businesses, audits_per_period, period, allow_consultant_workflow"
          )
          .order("price_usd_cents", { ascending: true });

        if (!mounted) return;

        if (error) {
          setPlansState({ status: "error", rows: [], error: error.message });
          return;
        }

        const rows = Array.isArray(data) ? data : [];
        const active = rows.filter((r) => r && r.is_active === true);

        setPlansState({ status: "ready", rows: active, error: null });
      } catch (e) {
        if (!mounted) return;
        setPlansState({
          status: "error",
          rows: [],
          error: String(e?.message || e || "Failed to load plans"),
        });
      }
    })();

    return () => {
      mounted = false;
    };
  }, []);

  function goUniversal() {
    navigate("/universal-audit");
  }

  async function startCheckout(planKey) {
    try {
      const base = String(process.env.REACT_APP_SUPABASE_FUNCTIONS_BASE || "").replace(/\/$/, "");
      if (!base) {
        window.alert(
          "Checkout is not configured.\n\nMissing env var:\nREACT_APP_SUPABASE_FUNCTIONS_BASE\n\nExample:\nhttps://YOUR_PROJECT_REF.functions.supabase.co"
        );
        return;
      }

      const anonKey = String(process.env.REACT_APP_SUPABASE_ANON_KEY || "").trim();
      if (!anonKey) {
        window.alert(
          "Checkout is not configured.\n\nMissing env var:\nREACT_APP_SUPABASE_ANON_KEY\n\nThis is required to call Supabase Edge Functions."
        );
        return;
      }

      const { data } = await supabase.auth.getSession();
      const accessToken = data?.session?.access_token;

      // Pricing is a FREE route; auth is only required to initiate checkout.
      if (!accessToken) {
        const returnTo = `/pricing${location.search || ""}`;
        navigate(`/auth?next=${encodeURIComponent(returnTo)}`, { replace: false });
        return;
      }

      const url = `${base}/create_checkout_session`;

      // Optional: include next_path so the Edge Function can (if implemented) embed it into success_url/cancel_url.
      // Safe even if the function ignores unknown fields.
      const next_path = nextParam && nextParam.startsWith("/") ? nextParam : "/dashboard";

      const res = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          apikey: anonKey,
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({ plan_key: String(planKey), next_path }),
      });

      const rawText = await res.text();
      let dataJson = null;
      try {
        dataJson = rawText ? JSON.parse(rawText) : null;
      } catch {
        dataJson = null;
      }

      if (!res.ok) {
        console.error("create_checkout_session failed", {
          status: res.status,
          statusText: res.statusText,
          body: dataJson ?? rawText,
        });

        const maybeMsg =
          (dataJson && (dataJson.message || dataJson.error || dataJson.details)) ||
          rawText ||
          "Unknown error";

        window.alert(
          `Checkout failed.\n\nStatus: ${res.status}\nMessage: ${String(maybeMsg).slice(0, 300)}\n\nOpen DevTools Console for details.`
        );
        return;
      }

      const checkoutUrl = dataJson?.checkout_url;
      if (!checkoutUrl) {
        console.error("Missing checkout_url in response", { parsed: dataJson, rawText });
        window.alert("Checkout failed: missing checkout URL.");
        return;
      }

      window.location.href = checkoutUrl;
    } catch (err) {
      console.error("Checkout error", err);
      window.alert("Checkout error. Check console for details.");
    }
  }

  const orderedPlans = useMemo(() => {
    const rows = plansState.rows || [];
    const order = ["precision_pass", "pro", "enterprise", "global_pro"];
    const byKey = new Map(rows.map((r) => [String(r.plan_key), r]));
    const out = [];

    for (const k of order) {
      if (byKey.has(k)) out.push(byKey.get(k));
    }

    for (const r of rows) {
      const k = String(r?.plan_key || "");
      if (!k) continue;
      if (!order.includes(k)) out.push(r);
    }

    return out.filter(Boolean);
  }, [plansState.rows]);

  const defaultSubscribeKey = useMemo(() => {
    const keys = new Set((plansState.rows || []).map((r) => String(r?.plan_key || "")));
    return keys.has("pro") ? "pro" : (plansState.rows?.[0]?.plan_key || "");
  }, [plansState.rows]);

  const currentPlanDisplayName = useMemo(() => {
    if (!currentPlanKey) return "";
    const match = (plansState.rows || []).find((p) => String(p?.plan_key || "") === currentPlanKey);
    return String(match?.display_name || currentPlanKey);
  }, [currentPlanKey, plansState.rows]);

  return (
    <div className="flex h-screen bg-slate-50 text-slate-900 overflow-hidden">
      <Sidebar />

      <main className="flex-1 overflow-auto px-8 md:px-12 py-10">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col gap-6 lg:flex-row lg:items-start lg:justify-between">
            <div className="max-w-3xl">
              <div className="flex flex-wrap items-center gap-2">
                <span className={badgeClasses("default")}>Plans</span>

                {/* Minimal entitlement status indicator (no layout change; just informational) */}
                {entLoading ? (
                  <span className="text-[11px] font-bold text-slate-500">Checking subscription…</span>
                ) : isPaid ? (
                  <span className="text-[11px] font-bold text-emerald-700">
                    Active: <span className="font-black">{currentPlanDisplayName}</span>
                    {paidStatus ? (
                      <span className="text-slate-400 font-semibold"> ({String(paidStatus)})</span>
                    ) : null}
                  </span>
                ) : (
                  <span className="text-[11px] font-bold text-slate-500">No active subscription detected</span>
                )}

                <button
                  type="button"
                  onClick={() => refreshEntitlement()}
                  className="ml-1 inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1 text-[11px] font-extrabold text-slate-700 hover:bg-slate-50"
                  title="Refresh subscription status"
                >
                  <RefreshCw size={12} />
                  Refresh
                </button>
              </div>

              <h1 className="mt-4 text-3xl md:text-4xl font-black tracking-tight text-slate-900">
                Choose the right level of defensibility
              </h1>

              <p className="mt-2 text-sm text-slate-500 font-semibold">
                Start with a free Universal Audit, then unlock Legal Mode, exports, and remediation tracking.
              </p>

              <div className="mt-5 flex flex-col sm:flex-row sm:items-center gap-3">
                <button
                  onClick={goUniversal}
                  className="inline-flex items-center justify-center gap-2 rounded-2xl bg-slate-900 px-5 py-3 text-sm font-extrabold text-white hover:bg-slate-800"
                >
                  Run Universal Audit
                  <ArrowRight size={16} />
                </button>

                <button
                  onClick={() => {
                    if (defaultSubscribeKey) startCheckout(defaultSubscribeKey);
                  }}
                  className="inline-flex items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-5 py-3 text-sm font-extrabold text-slate-800 hover:bg-slate-50"
                >
                  Subscribe Now
                  <Scale size={16} />
                </button>
              </div>

              {nextParam ? (
                <div className="mt-2 text-[11px] text-slate-400">
                  After subscribing you’ll return to: <span className="font-mono">{nextParam}</span>
                </div>
              ) : null}
            </div>

            <button
              onClick={() => navigate("/dashboard")}
              className="rounded-2xl border border-slate-200 bg-white px-5 py-3 text-sm font-extrabold uppercase tracking-widest text-slate-700 hover:bg-slate-50"
            >
              Back to Dashboard
            </button>
          </div>

          <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="rounded-2xl border border-slate-200 bg-white p-5">
              <div className="flex items-center gap-2 text-sm font-black text-slate-900">
                <ShieldCheck size={18} />
                Governance-first
              </div>
              <div className="mt-2 text-sm text-slate-600">
                Designed for board and regulator defensibility — not just checklists.
              </div>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white p-5">
              <div className="flex items-center gap-2 text-sm font-black text-slate-900">
                <Sparkles size={18} />
                Multi-regime exposure
              </div>
              <div className="mt-2 text-sm text-slate-600">
                Model simultaneous exposure across regimes without FX-summing in early-stage planning.
              </div>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white p-5">
              <div className="flex items-center gap-2 text-sm font-black text-slate-900">
                <Scale size={18} />
                Traceable methodology
              </div>
              <div className="mt-2 text-sm text-slate-600">
                Legal Mode exposes the reasoning, factors, and citations underpinning outputs.
              </div>
            </div>
          </div>

          <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {plansState.status === "error" ? (
              <div className="rounded-2xl border border-amber-200 bg-amber-50 p-6 text-sm text-amber-950 lg:col-span-4">
                <div className="font-black">Unable to load pricing plans.</div>
                <div className="mt-2 text-xs text-amber-900/80">
                  Error: {String(plansState.error || "Unknown error")}
                </div>
                <div className="mt-3 text-xs text-amber-900/80">You can still run the free Universal Audit.</div>
              </div>
            ) : plansState.status === "loading" ? (
              <div className="rounded-2xl border border-slate-200 bg-white p-6 text-sm text-slate-600 lg:col-span-4">
                Loading plans…
              </div>
            ) : orderedPlans.length === 0 ? (
              <div className="rounded-2xl border border-slate-200 bg-white p-6 text-sm text-slate-600 lg:col-span-4">
                No plans are available to display right now.
              </div>
            ) : (
              orderedPlans.map((plan) => {
                const ui = uiForPlanKey(plan.plan_key);
                const Icon = ui.icon;

                const isCurrent = currentPlanKey && String(plan.plan_key) === String(currentPlanKey);

                const flagBadge =
                  ui.flag === "Most Popular"
                    ? badgeClasses("popular")
                    : ui.flag === "Best for Consultants"
                    ? badgeClasses("consultant")
                    : null;

                const billingLabel = labelForBillingType(plan.billing_type, plan.period);

                const note =
                  String(plan.billing_type || "").toLowerCase() === "one_time" ? "24-hour access window" : ui.note;

                const features = buildFeaturesFromPlanRow(plan);

                return (
                  <div
                    key={String(plan.plan_key)}
                    className={`relative bg-white rounded-2xl border-2 p-6 transition-all duration-300 ${cardBorder(
                      ui.flag,
                      isCurrent
                    )}`}
                  >
                    {/* Top badge row */}
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 flex gap-2">
                      {ui.flag ? <span className={flagBadge}>{ui.flag}</span> : null}
                      {isCurrent ? <span className={badgeClasses("current")}>Current Plan</span> : null}
                    </div>

                    <div className="mb-6 pt-2">
                      <div className="flex items-center gap-2 mb-2">
                        <Icon className="w-5 h-5 text-slate-900" />
                        <h3 className="text-lg font-black text-slate-900">
                          {plan.display_name || ui.descriptionFallback}
                        </h3>
                      </div>
                      <p className="text-sm text-slate-500">{ui.descriptionFallback}</p>
                    </div>

                    <div className="mb-6">
                      <div className="flex items-baseline gap-2">
                        <span className="text-3xl font-extrabold text-slate-900">
                          ${moneyFromCents(plan.price_usd_cents)}
                        </span>
                        <span className="text-slate-500 text-sm">{billingLabel}</span>
                      </div>

                      {note ? (
                        <div className="flex items-center gap-2 mt-2 text-xs text-amber-700">
                          <Clock className="w-3 h-3" />
                          <span>{note}</span>
                        </div>
                      ) : null}

                      <div className="mt-2 text-[11px] text-slate-400">
                        Pricing shown is pulled from Supabase plans — editable anytime.
                      </div>
                    </div>

                    <ul className="space-y-2 mb-6">
                      {features.map((feature, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm">
                          {feature.included ? (
                            <Check className="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5" />
                          ) : (
                            <Lock className="w-4 h-4 text-slate-300 flex-shrink-0 mt-0.5" />
                          )}
                          <span className={feature.included ? "text-slate-700" : "text-slate-400"}>
                            {feature.text}
                          </span>
                        </li>
                      ))}
                    </ul>

                    <div className="space-y-2">
                      <button
                        onClick={() => {
                          if (!isCurrent) startCheckout(plan.plan_key);
                        }}
                        disabled={Boolean(isCurrent)}
                        className={`w-full rounded-xl px-4 py-3 text-sm font-extrabold transition-all ${
                          isCurrent
                            ? "bg-slate-200 text-slate-500 cursor-not-allowed"
                            : ui.flag === "Best for Consultants"
                            ? "bg-amber-500 text-white hover:bg-amber-600"
                            : "bg-slate-900 text-white hover:bg-slate-800"
                        }`}
                      >
                        {isCurrent ? "Current Plan" : "Subscribe Now"}
                      </button>

                      <button
                        onClick={goUniversal}
                        className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm font-extrabold text-slate-900 hover:bg-slate-50"
                      >
                        Run Universal Audit
                      </button>

                      {!authed ? (
                        <div className="text-[11px] text-slate-400">
                          Tip: Supabase login is required before checkout will work.
                        </div>
                      ) : null}
                    </div>
                  </div>
                );
              })
            )}
          </div>

          <div className="mt-14 rounded-2xl border border-slate-200 bg-white p-6">
            <div className="text-sm font-black text-slate-900">Operational note</div>
            <div className="mt-2 text-sm text-slate-600">
              This page creates Stripe Checkout sessions via a Supabase Edge Function. If checkout doesn’t launch, check:
              <span className="font-black"> REACT_APP_SUPABASE_FUNCTIONS_BASE</span> and Supabase login state.
            </div>
          </div>

          <div className="mt-8 pb-10 text-center text-[11px] text-slate-400">
            © 2026 Advanced Advisory Services, LLC. All Rights Reserved.
          </div>
        </div>
      </main>
    </div>
  );
}